import * as React from 'react';
import {useRef, useState} from 'react';
import {KatDropdown, KatInput, KatLink, KatTextarea} from '@amzn/katal-react';
import {FieldData, TableRow} from 'src/app/model/lighthouse/Common';
import {FieldType} from 'src/app/constant/Enums';

export interface FieldRendererProps {
    field: FieldData;
    onChange?: (label: string, value: string) => void;
    readOnly?: boolean;
}

/**
 * Renders a single FieldData item based on its type.
 * Supports: text, link, radio, dropdown, number, date, textarea, freeform.
 * Handles both read-only display and editable inputs using Katal components.
 */
const FieldRenderer: React.FC<FieldRendererProps> = ({field, onChange, readOnly}) => {
    const inputRef = useRef<any>(null);
    const isEditable = field.editable && !readOnly;

    const handleChange = (value: string) => {
        if (onChange) {
            onChange(field.label, value);
        }
    };

    const requiredMark = field.required ? <span className="lighthouse-required">*</span> : null;

    switch (field.type) {
        case FieldType.TEXT:
            if (isEditable) {
                return (
                    <div className="lighthouse-field-inline" data-testid={`field-text-${field.label}`}>
                        <span className="lighthouse-field-label">{field.label}{requiredMark}</span>
                        <KatInput
                            value={field.value || ''}
                            ref={inputRef}
                            onChange={/* istanbul ignore next */ () => handleChange(inputRef.current?.value || '')}
                        />
                    </div>
                );
            }
            return (
                <div className="lighthouse-field-readonly" data-testid={`field-text-${field.label}`}>
                    <span className="lighthouse-field-label">{field.label}:</span>
                    <span className="lighthouse-field-value">{field.value || '—'}</span>
                </div>
            );

        case FieldType.LINK:
            // No href means nothing to link to (e.g. Alert Count for a DP with no SSR alerts), so
            // render plain text. A '#' fallback made "—" a link that reopened the current page.
            if (!field.href) {
                return (
                    <div className="lighthouse-field-readonly" data-testid={`field-link-${field.label}`}>
                        <span className="lighthouse-field-label">{field.label}:</span>
                        <span className="lighthouse-field-value">{field.value || '—'}</span>
                        {field.trailingText && <span className="lighthouse-field-value">{field.trailingText}</span>}
                    </div>
                );
            }
            return (
                <div className="lighthouse-field-readonly" data-testid={`field-link-${field.label}`}>
                    <span className="lighthouse-field-label">{field.label}:</span>
                    <KatLink href={field.href} target="_blank" label={field.value || field.href} />
                    {field.trailingText && <span className="lighthouse-field-value">{field.trailingText}</span>}
                </div>
            );

        case FieldType.RADIO:
            if (isEditable) {
                return (
                    <div className="lighthouse-field-inline" data-testid={`field-radio-${field.label}`}>
                        <span className="lighthouse-field-label">{field.label}{requiredMark}</span>
                        <span className="lighthouse-radio-options">
                            {(field.options || []).map(opt => (
                                <label key={opt} className="lighthouse-radio-option">
                                    <input
                                        type="radio"
                                        name={`radio-${field.label}`}
                                        value={opt}
                                        checked={field.value === opt}
                                        onChange={() => handleChange(opt)}
                                    />
                                    {opt}
                                </label>
                            ))}
                        </span>
                    </div>
                );
            }
            return (
                <div className="lighthouse-field-readonly" data-testid={`field-radio-${field.label}`}>
                    <span className="lighthouse-field-label">{field.label}:</span>
                    <span className="lighthouse-field-value">{field.value || '—'}</span>
                </div>
            );

        case FieldType.DROPDOWN:
            if (isEditable) {
                const dropdownOptions = (field.options || []).map(opt => ({
                    name: opt,
                    value: opt
                }));
                return (
                    <div className="lighthouse-field-inline" data-testid={`field-dropdown-${field.label}`}>
                        <span className="lighthouse-field-label">{field.label}{requiredMark}</span>
                        <KatDropdown
                            placeholder="Select..."
                            options={dropdownOptions}
                            value={field.value || undefined}
                            ref={inputRef}
                            onChange={/* istanbul ignore next */ () => handleChange(inputRef.current?.value || '')}
                        />
                    </div>
                );
            }
            return (
                <div className="lighthouse-field-readonly" data-testid={`field-dropdown-${field.label}`}>
                    <span className="lighthouse-field-label">{field.label}:</span>
                    <span className="lighthouse-field-value">{field.value || '—'}</span>
                </div>
            );

        case FieldType.NUMBER:
            if (isEditable) {
                return (
                    <div className="lighthouse-field-inline" data-testid={`field-number-${field.label}`}>
                        <span className="lighthouse-field-label">{field.label}{requiredMark}</span>
                        <KatInput
                            type="number"
                            value={field.value || ''}
                            ref={inputRef}
                            onChange={/* istanbul ignore next */ () => handleChange(inputRef.current?.value || '')}
                        />
                    </div>
                );
            }
            return (
                <div className="lighthouse-field-readonly" data-testid={`field-number-${field.label}`}>
                    <span className="lighthouse-field-label">{field.label}:</span>
                    <span className="lighthouse-field-value">{field.value || '—'}</span>
                </div>
            );

        case FieldType.DATE:
            return (
                <div className="lighthouse-field-readonly" data-testid={`field-date-${field.label}`}>
                    <span className="lighthouse-field-label">{field.label}:</span>
                    <span className="lighthouse-field-value">
                        {field.value ? new Date(field.value).toLocaleDateString() : '—'}
                    </span>
                </div>
            );

        case FieldType.TEXTAREA:
            if (isEditable) {
                return (
                    <div className="lighthouse-field-inline" data-testid={`field-textarea-${field.label}`}>
                        <span className="lighthouse-field-label">{field.label}{requiredMark}</span>
                        <KatTextarea
                            value={field.value || ''}
                            ref={inputRef}
                            onChange={/* istanbul ignore next */ () => handleChange(inputRef.current?.value || '')}
                        />
                    </div>
                );
            }
            return (
                <div className="lighthouse-field-readonly" data-testid={`field-textarea-${field.label}`}>
                    <span className="lighthouse-field-label">{field.label}:</span>
                    <span className="lighthouse-field-value">{field.value || '—'}</span>
                </div>
            );

        case FieldType.FREEFORM:
            return <FreeformField field={field} onChange={handleChange} isEditable={!!isEditable} requiredMark={requiredMark} />;

        case FieldType.TABLE:
            return <TableField field={field} />;

        default:
            return (
                <div className="lighthouse-field-readonly">
                    <span className="lighthouse-field-label">{field.label}:</span>
                    <span className="lighthouse-field-value">{field.value || 'N/A'}</span>
                </div>
            );
    }
};

/** Rows per page. Matches DefectsPanel's default so both tables page identically. */
const TABLE_PAGE_SIZE = 50;

/**
 * TableField — renders a TABLE field's `columns`/`rows` as a sortable, paginated table. Fully
 * generic: headers, cell types and ordering all come from the payload, and each cell is delegated
 * to FieldRenderer, so onboarding a new table is a backend config row with no change here.
 */
const TableField: React.FC<{field: FieldData}> = ({field}) => {
    const columns = field.columns ?? [];
    const rows = field.rows ?? [];

    const [sortColumn, setSortColumn] = useState<number | null>(null);
    const [sortAscending, setSortAscending] = useState(true);
    const [page, setPage] = useState(1);

    const cellText = (row: TableRow, index: number) => row.cells?.[index]?.value ?? '';

    const sortedRows = React.useMemo(() => {
        if (sortColumn === null) return rows;
        const values = rows.map(row => cellText(row, sortColumn));
        // Numeric only when every non-blank cell is a number, so "9" doesn't sort after "10".
        const numeric = values.some(v => v !== '')
            && values.every(v => v === '' || !isNaN(Number(v)));
        const copy = [...rows];
        copy.sort((a, b) => {
            const left = cellText(a, sortColumn);
            const right = cellText(b, sortColumn);
            if (left === '' || right === '') {
                return left === right ? 0 : (left === '' ? 1 : -1);
            }
            const compared = numeric
                ? Number(left) - Number(right)
                : left.localeCompare(right);
            return sortAscending ? compared : -compared;
        });
        return copy;
    }, [rows, sortColumn, sortAscending]);

    const totalPages = Math.max(1, Math.ceil(sortedRows.length / TABLE_PAGE_SIZE));
    const currentPage = Math.min(page, totalPages);
    const visibleRows = sortedRows.slice(
        (currentPage - 1) * TABLE_PAGE_SIZE,
        currentPage * TABLE_PAGE_SIZE
    );

    const toggleSort = (index: number) => {
        if (sortColumn === index) {
            setSortAscending(prev => !prev);
        } else {
            setSortColumn(index);
            setSortAscending(true);
        }
        setPage(1);
    };

    const sortIndicator = (index: number) => {
        if (sortColumn !== index) return '';
        return sortAscending ? ' ▲' : ' ▼';
    };

    return (
        <div className="lighthouse-field-table" data-testid={`field-table-${field.label}`}>
            <div className="lighthouse-table-header">
                <span className="lighthouse-field-label">{field.label}</span>
                <span className="lighthouse-table-count">{rows.length}</span>
            </div>

            {columns.length === 0 ? (
                <p className="lighthouse-table-empty" data-testid="table-no-columns">
                    No columns configured.
                </p>
            ) : (
                <>
                    <table className="lighthouse-table">
                        <thead>
                            <tr>
                                {columns.map((column, index) => (
                                    <th key={`${column.label}-${index}`}
                                        onClick={() => toggleSort(index)}
                                        className="lighthouse-table-sortable"
                                        data-testid={`table-col-${column.label}`}>
                                        {column.label}{sortIndicator(index)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {visibleRows.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                    {columns.map((column, colIndex) => {
                                        const cell = row.cells?.[colIndex];
                                        return (
                                            <td key={colIndex}>
                                                {cell
                                                    // Cells are values, never editable: a label would
                                                    // repeat the column header on every row, so blank it.
                                                    ? <FieldRenderer field={{...cell, label: '', editable: false}} readOnly />
                                                    : '—'}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {rows.length === 0 && (
                        <p className="lighthouse-table-empty" data-testid="table-no-rows">
                            No records.
                        </p>
                    )}

                    {totalPages > 1 && (
                        <div className="lighthouse-table-pagination">
                            <button disabled={currentPage === 1}
                                    onClick={() => setPage(currentPage - 1)}
                                    data-testid="table-prev-page">Previous</button>
                            <span>Page {currentPage} of {totalPages}</span>
                            <button disabled={currentPage === totalPages}
                                    onClick={() => setPage(currentPage + 1)}
                                    data-testid="table-next-page">Next</button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

/**
 * FreeformField — Inline field with "Add" link that opens a popup textarea.
 * Click tick (✓) to save and close popup.
 */
const FreeformField: React.FC<{
    field: FieldData;
    onChange: (value: string) => void;
    isEditable: boolean;
    requiredMark: React.ReactNode;
}> = ({field, onChange, isEditable, requiredMark}) => {
    const [showPopup, setShowPopup] = useState(false);
    const [text, setText] = useState(field.value || '');
    const [savedValue, setSavedValue] = useState(field.value || '');

    const handleSave = () => {
        setSavedValue(text);
        onChange(text);
        setShowPopup(false);
    };

    // Show only first 5 words as preview
    const getPreview = (val: string) => {
        if (!val) return '';
        const words = val.split(/\s+/);
        if (words.length <= 5) return val;
        return words.slice(0, 5).join(' ') + '...';
    };

    return (
        <div className="lighthouse-field-inline" data-testid={`field-freeform-${field.label}`}>
            <span className="lighthouse-field-label">{field.label}{requiredMark}:</span>
            <span className="lighthouse-field-value">{getPreview(savedValue)}</span>
            {isEditable && (
                // The anchor is the popup's positioning context, so the popup opens beside its Add button.
                <span className="lighthouse-freeform-anchor">
                    <button
                        className="lighthouse-edit-link"
                        onClick={() => setShowPopup(true)}
                        aria-label={`Add ${field.label}`}
                    >
                        Add
                    </button>
                    {showPopup && (
                        <div className="lighthouse-freeform-popup">
                            <textarea
                                className="lighthouse-freeform-textarea"
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                placeholder="Enter text..."
                                autoFocus
                            />
                            <button className="lighthouse-freeform-save" onClick={handleSave} title="Save">✓</button>
                        </div>
                    )}
                </span>
            )}
        </div>
    );
};

export default FieldRenderer;
